/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Asks the server whether this visitor already holds an entry for one event.
 *
 * WHY THIS EXISTS. The browser's own memory of a registration is per-device: a
 * cleared cache or a different phone forgets it, and the visitor is offered a
 * Register button they will only be refused. This is the answer that survives
 * both, because it comes from the rows themselves.
 *
 * IT FILTERS BY THE PERSON EXPLICITLY, and that is not belt-and-braces. The
 * batch read endpoint applies each table's own auto-filters — soft delete, the
 * public filter expression — but it does NOT apply row-level security, so no
 * scoping happens on its own. Without `created_by_id` in the where clause this
 * would ask "has anyone entered this event", and hide the button from everybody
 * the moment one person registered.
 *
 * IT FAILS TO "DO NOT KNOW", NEVER TO "REGISTERED". Every failure — no read
 * permission, a network fault, a shape that does not parse — returns null, and
 * the caller falls back to whatever it knew before. That direction is chosen
 * deliberately: the cost of a wrong "not registered" is one refused click, and
 * the database still refuses the write. The cost of a wrong "registered" is a
 * visitor who cannot enter an event and has no way to find out why.
 */

import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';

const api = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export const REGISTRATION_TABLE = 'event_registration';

/** The statuses that mean a place is actually held. */
export const ACTIVE_STATUSES = ['CONFIRMED', 'PENDING'];

/** Names this operation's block in the reply, so it is read by name. */
const RESULT_KEY = 'mine';

/**
 * How many active registrations this person holds for this event.
 *
 * Returns a number, or null when the question could not be answered.
 *
 * ONE RETRY, and only for a genuine fault. A refusal — no read permission, a
 * block the server marked 'error' — is a settled answer and asking again just
 * repeats it. A thrown call is not settled: a dropped connection or a token
 * that expired between the check and the call is exactly the case where the
 * second attempt succeeds, and where giving up quietly would let somebody
 * register a second time.
 */
export async function fetchMyRegistrations({ eventId, applicationUserId, retries = 1 }) {
  // Both are required. Asking without the person would count everybody's.
  if (eventId === undefined || eventId === null || eventId === '') return null;
  if (!applicationUserId) return null;

  try {
    const resp = await api.apiCall('/arcee/application/table/read/records/', {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        organization_id: envConfig.organizationId,
        application_id: envConfig.applicationId,
        operations: [{
          // Named, so the reply can be found by name rather than by position.
          ref: RESULT_KEY,
          table: REGISTRATION_TABLE,
          // `id` only: nothing else is needed to answer "any?", and asking for
          // less is the right habit on a table of other people's entries.
          fields: ['id'],
          // A `where` is an object; a list value becomes an IN.
          where: {
            event_id: eventId,
            created_by_id: applicationUserId,
            status: ACTIVE_STATUSES,
          },
          limit: 1,
        }],
      }),
    });

    // 403 is the expected answer until participants are granted read on this
    // table. Not an error to report — just an unanswerable question.
    if (!resp.ok) return null;

    const body = await resp.json().catch(() => null);
    // A read batch answers as {results: {<ref>: {status, data}}} — unlike a
    // write batch, which answers with an ordered operations list. One failing
    // block is marked 'error' and leaves the others intact, so the status is
    // checked rather than assumed.
    const block = body && body.results && body.results[RESULT_KEY];
    if (!block || block.status === 'error') return null;

    return Array.isArray(block.data) ? block.data.length : null;
  } catch (e) {
    // APIHelper refreshes an expired OAuth token and retries once on its own,
    // so reaching here means that failed too, or the session went, or the
    // network did. One more attempt covers the transient cases; beyond that,
    // repeating it only delays an answer we are not going to get.
    if (retries > 0) {
      return fetchMyRegistrations({ eventId, applicationUserId, retries: retries - 1 });
    }
    return null;
  }
}

/**
 * Whether the page should treat this visitor as already entered.
 *
 * `null` from the fetch means "could not tell", which is why this is a separate
 * one-line function rather than a truthiness check at the call site: `0` and
 * `null` are different answers and must not collapse into the same `false`.
 */
export function holdsAnEntry(count) {
  return typeof count === 'number' && count > 0;
}

/**
 * Which activity each of this person's registrations is for, by registration id.
 * `eventId` is optional: My Registrations asks for every pass at once.
 *
 * The pass view carries `registration_id` but not the activity — event_registration
 * is not a public table, so the view cannot follow it there. This reads the
 * person's own rows for that one column; the caller turns the activity ids into
 * names from the event's own public activities. Scoped by `created_by_id` for
 * the same reason as above.
 *
 * Returns {registrationId: activityId}, or null when it could not be asked. A
 * missing name only means a line is not drawn, so nothing here is retried.
 */
export async function fetchRegistrationActivities({ eventId, applicationUserId, registrationIds }) {
  const ids = Array.from(new Set((registrationIds || []).filter((id) => id !== null && id !== undefined && id !== '')));
  if (!applicationUserId || !ids.length) return null;
  const hasEvent = !(eventId === undefined || eventId === null || eventId === '');

  // The read endpoint answers at most READ_CHUNK rows an operation, so a long
  // list goes as several operations of one request.
  const chunks = [];
  for (let i = 0; i < ids.length; i += READ_CHUNK) chunks.push(ids.slice(i, i + READ_CHUNK));

  try {
    const resp = await api.apiCall('/arcee/application/table/read/records/', {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        organization_id: envConfig.organizationId,
        application_id: envConfig.applicationId,
        operations: chunks.map((chunk, index) => ({
          ref: `regs${index}`,
          table: REGISTRATION_TABLE,
          fields: ['id', 'event_activity_id'],
          // `eventId` narrows to one event; without it, every listed id.
          where: hasEvent
            ? { event_id: eventId, created_by_id: applicationUserId, id: chunk }
            : { created_by_id: applicationUserId, id: chunk },
          limit: chunk.length,
        })),
      }),
    });
    if (!resp.ok) return null;

    const body = await resp.json().catch(() => null);
    const results = body && body.results;
    if (!results) return null;

    const out = {};
    chunks.forEach((chunk, index) => {
      const block = results[`regs${index}`];
      if (!block || block.status === 'error' || !Array.isArray(block.data)) return;
      block.data.forEach((row) => {
        const activity = row && row.event_activity_id;
        // A relation can come back as {id} or as {related_table_name, data: {id}}.
        const activityId = activity && typeof activity === 'object'
          ? (activity.id ?? activity.data?.id) : activity;
        if (row && row.id != null && activityId != null) out[String(row.id)] = String(activityId);
      });
    });
    return out;
  } catch (e) {
    return null;
  }
}

/** The batch read's own per-operation row cap (MAX_ROWS_PER_OPERATION). */
const READ_CHUNK = 200;
